import { useState } from "react"
import { Check, Luggage, Backpack } from "lucide-react"
import { Button } from "@/components/ui/button"
import { formatDateLabel, formatDayLabel, formatDuration, formatPriceDelta, dayOffset } from "@/lib/booking"
import type { FlightOptionView } from "@/types/booking"
import { BaggageInfoModal } from "@/components/BaggageInfoModal"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { resolveAirlineName } from "@/lib/airlines"

interface FlightCardProps {
  flight: FlightOptionView
  visuallySelected: boolean
  isIncludedInPackage: boolean
  currency: string
  departureLabel: string
  destinationLabel: string
  selectedPriceDiffPerPerson: number
  nightCount: number
  onSelect: () => void
}

export function FlightCard({
  flight,
  visuallySelected,
  isIncludedInPackage,
  currency,
  departureLabel,
  destinationLabel,
  selectedPriceDiffPerPerson,
  nightCount,
  onSelect,
}: FlightCardProps) {
  const [baggageModalOpen, setBaggageModalOpen] = useState(false)
  const cardBorder = visuallySelected
    ? "border-primary/60"
    : "border-border/90 hover:border-border"

  let priceLabel: string
  let showPrice: boolean

  if (isIncludedInPackage) {
    priceLabel = "Included in package"
    showPrice = true
  } else if (visuallySelected) {
    showPrice = false
    priceLabel = ""
  } else {
    const diff =
      selectedPriceDiffPerPerson === 0
        ? flight.priceDiffPerPerson
        : flight.priceDiffPerPerson - selectedPriceDiffPerPerson
    priceLabel = formatPriceDelta(diff, currency, true)
    showPrice = true
  }

  const airlines = Array.from(
    new Set([...flight.outbound.airline, ...flight.inbound.airline])
  )

  const isDirect =
    flight.outbound.stops === 0 && flight.inbound.stops === 0

  return (
    <>
      <BaggageInfoModal
        open={baggageModalOpen}
        onOpenChange={setBaggageModalOpen}
        baggageIncluded={flight.baggageIncluded}
      />
      <Card
        className={`border-2 ring-0 transition-all rounded-[18px] ${cardBorder}`}
      >
        <CardContent className="p-4 @min-[48rem]:p-5">
          <div className="flex flex-col gap-1.5 @min-[48rem]:flex-row @min-[48rem]:items-start @min-[48rem]:gap-4">
            <div className="order-2 flex min-w-0 flex-1 flex-col gap-0.5 @min-[48rem]:order-1">
              {isDirect && (
                <span className="text-[9px] font-bold tracking-widest text-primary uppercase @min-[48rem]:text-[11px]">
                  Direct
                </span>
              )}
              <span className="text-[11px] leading-none text-foreground/90 @min-[48rem]:text-[15px]">
                {departureLabel}
              </span>
              <span className="text-[18px] leading-tight font-bold text-foreground uppercase @min-[48rem]:text-[28px]">
                {destinationLabel}
              </span>
            </div>

            <div className="order-1 flex justify-between gap-1 @min-[48rem]:order-2 @min-[48rem]:ml-auto @min-[48rem]:shrink-0 @min-[48rem]:flex-wrap @min-[48rem]:justify-end @min-[48rem]:gap-1.5">
              <div className="scrollbar-none flex min-w-0 flex-1 gap-1 overflow-x-auto @min-[48rem]:[display:contents] @min-[48rem]:min-w-0 @min-[48rem]:overflow-visible">
                <Badge variant="outline" className="shrink-0 whitespace-nowrap text-[10px] @min-[48rem]:text-xs">
                  {nightCount} nights
                </Badge>

                <Badge variant="outline" className="shrink-0 whitespace-nowrap text-[10px] @min-[48rem]:text-xs">
                  {flight.outbound.travelClass}
                </Badge>

                {airlines.map((name) => (
                  <Badge variant="outline" key={name} className="shrink-0 whitespace-nowrap text-[10px] @min-[48rem]:text-xs">{resolveAirlineName(name)}</Badge>
                ))}
              </div>

              <div className="flex shrink-0 gap-1 @min-[48rem]:[display:contents]">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setBaggageModalOpen(true)}
                  className="shrink-0 text-[10px] @min-[48rem]:text-xs"
                >
                  <Backpack className="size-2.5 @min-[48rem]:size-3.5" />
                  <span>1</span>
                </Button>

                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setBaggageModalOpen(true)}
                  className="shrink-0 text-[10px] @min-[48rem]:text-xs"
                >
                  <Luggage className="size-2.5 @min-[48rem]:size-3.5" />
                  <span>{flight.baggageIncluded ? "1" : "0"}</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-4 @min-[48rem]:gap-5">
            {/* Outbound leg - mobile: info only. desktop: info + price in grid */}
            <div className="flex flex-col gap-2 @min-[48rem]:grid @min-[48rem]:grid-cols-[36px_100px_1fr_auto] @min-[48rem]:gap-x-4 @min-[48rem]:items-center">
              <div className="flex items-center gap-x-2 @min-[48rem]:[display:contents]">
                <AirlineMark />
                <div className="flex shrink-0 flex-col">
                  <span className="text-[10px] font-semibold tracking-wide text-primary uppercase @min-[48rem]:text-[12px]">
                    Outbound
                  </span>
                  <span className="text-[11px] leading-none font-medium text-foreground/70 @min-[48rem]:text-[13px]">
                    {flight.outbound.departureDate
                      ? `${formatDateLabel(flight.outbound.departureDate)} ${formatDayLabel(flight.outbound.departureDate)}`
                      : "TBA"}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-2 @min-[48rem]:gap-3">
                  <span className="text-[14px] leading-none font-bold text-foreground @min-[48rem]:text-[18px]">
                    {flight.outbound.departureTime || "TBA"}
                  </span>
                  <div className="flex min-w-[48px] flex-col items-center @min-[48rem]:min-w-[72px]">
                    <span className="text-[9px] font-medium tracking-wide text-foreground/60 uppercase @min-[48rem]:text-[11px]">
                      {formatDuration(flight.outbound.durationMinutes)}
                    </span>
                    <div className="my-0.5 h-px w-full bg-primary/50 @min-[48rem]:my-1" />
                    <span
                      className={`text-[9px] font-semibold uppercase @min-[48rem]:text-[11px] ${
                        flight.outbound.stops === 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-destructive"
                      }`}
                    >
                      {flight.outbound.stops === 0
                        ? "Direct"
                        : `${flight.outbound.stops} stop${flight.outbound.stops > 1 ? "s" : ""}`}
                    </span>
                  </div>
                  <span className="text-[14px] leading-none font-bold text-foreground @min-[48rem]:text-[18px]">
                    {flight.outbound.arrivalTime || "TBA"}
                  </span>
                  {dayOffset(flight.outbound) > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 leading-none md:text-[10px]">
                      +{dayOffset(flight.outbound)}
                    </Badge>
                  )}
                </div>
              </div>
              {/* Desktop only: price in grid's auto column */}
              {showPrice && (
                <div className="hidden @min-[48rem]:[display:contents]">
                  <span
                    className={`shrink-0 leading-none text-primary ${
                      isIncludedInPackage
                        ? "text-[12px] font-medium"
                        : "text-[22px] font-bold"
                    }`}
                  >
                    {priceLabel}
                  </span>
                </div>
              )}
            </div>

            {/* Inbound leg - mobile: info + price+button below.
                desktop: info + button in grid. */}
            <div className="flex flex-col gap-2 @min-[48rem]:grid @min-[48rem]:grid-cols-[36px_100px_1fr_auto] @min-[48rem]:gap-x-4 @min-[48rem]:items-center">
              <div className="flex items-center gap-x-2 @min-[48rem]:[display:contents]">
                <AirlineMark />
                <div className="flex shrink-0 flex-col">
                  <span className="text-[10px] font-semibold tracking-wide text-primary uppercase @min-[48rem]:text-[12px]">
                    Inbound
                  </span>
                  <span className="text-[11px] leading-none font-medium text-foreground/70 @min-[48rem]:text-[13px]">
                    {flight.inbound.departureDate
                      ? `${formatDateLabel(flight.inbound.departureDate)} ${formatDayLabel(flight.inbound.departureDate)}`
                      : "TBA"}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-2 @min-[48rem]:gap-3">
                  <span className="text-[14px] leading-none font-bold text-foreground @min-[48rem]:text-[18px]">
                    {flight.inbound.departureTime || "TBA"}
                  </span>
                  <div className="flex min-w-[48px] flex-col items-center @min-[48rem]:min-w-[72px]">
                    <span className="text-[9px] font-medium tracking-wide text-foreground/60 uppercase @min-[48rem]:text-[11px]">
                      {formatDuration(flight.inbound.durationMinutes)}
                    </span>
                    <div className="my-0.5 h-px w-full bg-primary/50 @min-[48rem]:my-1" />
                    <span
                      className={`text-[9px] font-semibold uppercase @min-[48rem]:text-[11px] ${
                        flight.inbound.stops === 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-destructive"
                      }`}
                    >
                      {flight.inbound.stops === 0
                        ? "Direct"
                        : `${flight.inbound.stops} stop${flight.inbound.stops > 1 ? "s" : ""}`}
                    </span>
                  </div>
                  <span className="text-[14px] leading-none font-bold text-foreground @min-[48rem]:text-[18px]">
                    {flight.inbound.arrivalTime || "TBA"}
                  </span>
                  {dayOffset(flight.inbound) > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 leading-none md:text-[10px]">
                      +{dayOffset(flight.inbound)}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Mobile: price + button stacked (matches HotelCard pattern) */}
              <div className="flex flex-col items-end gap-3 @min-[48rem]:hidden">
                {showPrice && (
                  <div className="w-full text-end">
                    <span
                      className={`leading-none text-primary ${
                        isIncludedInPackage
                          ? "text-[13px] font-medium text-foreground"
                          : "text-[22px] font-bold"
                      }`}
                    >
                      {priceLabel}
                    </span>
                  </div>
                )}
                <Button
                  onClick={onSelect}
                  variant={visuallySelected ? "outline" : "default"}
                  size="xl"
                  className="w-full"
                >
                  {visuallySelected && <Check className="size-4" />}
                  {visuallySelected ? "Selected" : "Select Flight"}
                </Button>
              </div>

              {/* Desktop only: button in grid's auto column */}
              <div className="hidden @min-[48rem]:[display:contents]">
                <Button
                  onClick={onSelect}
                  variant={visuallySelected ? "outline" : "default"}
                  size="xl"
                >
                  {visuallySelected && <Check className="size-5" />}
                  {visuallySelected ? "Selected" : "Select Flight"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

function AirlineMark() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      className="text-[#f7b631] @min-[48rem]:h-6 @min-[48rem]:w-6"
    >
      <path d="M2 13.4 22 4l-5.2 16.5-3.9-6.1L2 13.4Z" fill="currentColor" />
      <path
        d="m12.9 14.4 2.6 4.2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}
